import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { RootState, store } from '../../../redux/store';
import { connect } from 'pwa-helpers';
import {
  dashboardConfigurationSelector,
  DashboardUi,
  dashboardUiSelector,
  filtersPanelActiveStateSelector,
  fullScreenActiveStateSelector,
  selectShouldDisplayDiscardPopUp,
  toggleExportButtonSpinner,
  toggleFiltersPanelActiveState,
  toggleFullscreenActiveState,
  toggleHasWidgetsInErrorState,
  toggleLiveViewActiveState,
  toggleSettingsPanelActiveState,
  toggleWidgetDetailsPanelActiveState,
} from '../../redux/dashboards.ui.slice';
import { ifDefined } from 'lit/directives/if-defined.js';
import { GzDashboardSettingsPanelWebComponent } from '../../gzDashboardSettingsPanel';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { GzDashboardGridStackManagerWebComponent } from '../../gzDashboardGridStackManager';
import {
  createNewSmartView,
  discardSmartViewChanges,
  discardSmartViewChangesAsync,
  saveSmartViewConfiguration,
  selectActiveViewProps,
  selectAllSmartViews,
  selectAllSmartViewsCategories,
  selectGridStackReset,
  toggleSmartViewsPanelIsCollapsed,
} from '../../redux/dashboards.smart.views.slice';
import { DashboardPopUpFactory, DashboardPopUpType } from '../../utils/dashboard.pop-up.factory';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { FormValidator, ValidationResult } from '../../../ui/gzForm/formField/types/form-validator';
import {
  DashboardType,
  DashboardUiConfig,
  Filter,
  GetDashboardPdfRequest,
  GetDashboardPdfResponse,
  WidgetsPayloadMapData,
  PageFormat,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { GDSettingsPanelEvents } from '../../gzDashboardSettingsPanel/types';
import { CustomizableValidator } from '../../../ui/gzForm/validators/customizable.validator';
import { WEB_COMPONENT_DASHBOARD_MACRO_COMPONENT } from '../../../webcomponents.constants';
import { selectDashboardFilterConfigs, selectDashboardFilterValues } from '../../redux/dashboard.filters.slice';
import {
  selectActiveWidgets,
  selectRefreshNecessary,
  selectWidgetCategories,
  selectWidgets,
  setRefreshWidgets,
  updateWidgetsSearchText,
} from '../../redux/dashboards.widgets.slice';
import { GetWidgetWithOverviewResponse, WidgetState } from '@nirvana/shared/dtos/types/widgets/common';
import {
  addFullscreenEventListeners,
  ElementWithFullscreen,
  enterFullscreen,
  exitFullscreen,
  isBrowserFullscreen,
  onEnterFullscreenError,
  onExitFullscreenError,
  removeFullscreenEventListeners,
} from '../../../utils/fullscreen.utils';
import { removeElementClass, setElementClass } from '../../../utils/htmlElement.utils';
import { classMap } from 'lit/directives/class-map.js';
import {
  DashboardStreamRequest,
  DashboardStreamResponse,
  GetDashboardCsvRequest,
  GetDashboardCsvResponse,
} from '@nirvana/shared/dtos/types/widgets/gz-console';
import { FeedbackHelper } from '../../../utils/feedback/feedback.helper';
import { DashboardReduxWidget } from '../../redux/types';
import { MicroserviceRequest } from '../../../utils/request/microservice.request';
import {
  NirvanaErrorCode,
  NirvanaSanitizedError,
  StreamMessage,
  StreamMessageType,
} from '@nirvana/shared/dtos/types/shared/communication';
import { downloadBlob } from '../../../utils/files.utils';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { translateFn } from '../../../utils/translator.helper';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import { GzIconButtonWebComponent } from '../../../ui/gzIconButton';
import { getExecutiveSummaryParams } from '../../utils/executive.summary.helper';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { DashboardEventsListeners } from '../../types/types';
import { GzDashboardActionsToolbarWebComponent } from '../../gzDashboardActionsToolbar';
import { GzDashboardFiltersBarWebComponent } from '../../gzDashboardFilters';
import { GzDashboardTitleWithFeedBackToolbarWebComponent } from '../../gzDashboardTitleWithFeedBackToolbar';
import { MacroComponentEvents } from '../types';
import { WidgetEvents, WidgetStateFrontEnd, WidgetStateFrontEndToBackend } from '../../gzGenericWidget/types';
import { Rights } from '@nirvana/shared/constants';
import { DisplayMode } from '../../../types/common/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { FiltersHelper } from '../../gzDashboardFilters/helpers/filters.helper';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { RequestOptions } from '@nirvana/shared/websocket-client';

/**
 * @summary Type here the description of this component
 * @tag gz-dashboard-macro-component
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_DASHBOARD_MACRO_COMPONENT)
export class GzDashboardMacroComponentWebComponent extends connect(store)(LitElement) {
  @property()
  private isEarlyAccess?: boolean;

  @property()
  private showFeedbackFormBtn?: boolean;

  @state()
  private dashboardConfiguration: DashboardUiConfig;

  @state()
  private dashboardUi: DashboardUi;

  @state()
  private smartViewsValidators?: Array<FormValidator>;

  @state()
  private fullscreenActive: boolean;

  @state()
  private filtersPanelActive: boolean;

  @query('#dashboard-settings')
  private readonly dashboardSettingsRef: GzDashboardSettingsPanelWebComponent;

  @query('#grid-stack-manager')
  private readonly gridStackManagerRef: GzDashboardGridStackManagerWebComponent;

  @query('#dashboard-settings-side-menu')
  private readonly dashboardSettingsSideMenuRef: GzSideMenuWebComponent;

  @query('#dashboard-widget-details')
  private readonly dashboardWidgetDetailsRef: HTMLElement;

  @query('#dashboard-widget-details-side-menu')
  private readonly dashboardWidgetDetailsSideMenuRef: GzSideMenuWebComponent;

  @query('#dashboard-toolbar')
  private readonly dashboardToolbarRef: GzDashboardActionsToolbarWebComponent;

  @query('#dashboard-feedback-toolbar')
  private readonly dashboardFeedbackToolbarRef: GzDashboardTitleWithFeedBackToolbarWebComponent;

  @query('#fullscreen-content')
  private readonly fullscreenContent: ElementWithFullscreen;

  @query('#dashboard-filters-bar')
  private readonly dashboardFiltersBar: GzDashboardFiltersBarWebComponent;

  private sideMenuListenersAdded = false;

  private widgetsRefreshing = false;

  private shouldDisplayDiscardPopUp: boolean;

  private fullscreenChangeListener = this.onFullscreenChange.bind(this);

  private widgetsInLoading: Array<string> = [];

  private csvParts: Array<string> = [];

  private csvPrefix: string;

  public dashboardType: DashboardType;

  public displayMode: DisplayMode;

  public selectedTarget: Target;

  private widgetsLoadedForPdfEventName: string = MacroComponentEvents.ALL_WIDGETS_LOADED;

  private widgetsLoadedForPdf: Set<string> = new Set();

  private dependencies: any;

  private widgetDetailsComponent: any;

  private isCurrentlyRefreshing = false;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  firstUpdated() {
    addFullscreenEventListeners(this.fullscreenContent, this.fullscreenChangeListener);
    this.addEventListener(
      DashboardEventsListeners.WIDGET_DETAILS_CLICKED,
      this.handleWidgetDetailsSideMenuOpen.bind(this)
    );
    this.addEventListener(DashboardEventsListeners.CLOSE_WIDGET_DETAILS, this.closeWidgetDetailsSideMenu.bind(this));
    this.gridStackManagerRef.injectDependencies(this.dependencies);
    this.dashboardFiltersBar.injectDependencies(this.dependencies);
  }

  disconnectedCallback() {
    removeFullscreenEventListeners(this.fullscreenContent, this.fullscreenChangeListener);
    this.removeEventListener(
      DashboardEventsListeners.WIDGET_DETAILS_CLICKED,
      this.handleWidgetDetailsSideMenuOpen.bind(this)
    );
    this.removeEventListener(DashboardEventsListeners.CLOSE_WIDGET_DETAILS, this.closeWidgetDetailsSideMenu.bind(this));
    this.widgetsInLoading = [];
    super.disconnectedCallback();
  }

  injectDependencies(dependencies: any) {
    this.dependencies = dependencies;
  }

  async stateChanged(rootState: RootState) {
    if (
      this.dashboardConfiguration &&
      rootState.dashboardsApp.uiSlice.configuration.dashboardType !== this.dashboardType
    ) {
      return;
    }
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    this.dashboardConfiguration = dashboardConfigurationSelector(dashboardsAppState);
    this.dashboardUi = dashboardUiSelector(dashboardsAppState);
    this.shouldDisplayDiscardPopUp = selectShouldDisplayDiscardPopUp(dashboardsAppState);
    this.handleDashboardActionsToolbarEvents();
    this.handleDashboardWidgetsEvents();
    this.fullscreenActive = fullScreenActiveStateSelector(dashboardsAppState);
    this.filtersPanelActive = filtersPanelActiveStateSelector(dashboardsAppState);

    switch (this.displayMode) {
      case DisplayMode.DASHBOARD:
        break;
      case DisplayMode.PDF:
        if (this.dashboardToolbarRef) {
          setElementClass(this.dashboardToolbarRef, 'hidden');
        }
        break;
      case DisplayMode.REPORT:
        break;
      default:
        console.error(`Unknown display mode ${this.displayMode}`);
        return;
    }

    this.isEarlyAccess =
      this.dashboardConfiguration.isEarlyAccess === undefined ? false : this.dashboardConfiguration.isEarlyAccess;
    this.showFeedbackFormBtn =
      this.dashboardConfiguration.showFeedbackFormBtn === undefined
        ? false
        : this.dashboardConfiguration.showFeedbackFormBtn;
    this.smartViewsValidators = [
      RequiredValidator('This field is required.'),
      CustomizableValidator(this.validateViewName.bind(this), rootState),
    ];

    const shouldIgnoreLoadingWidgets = selectGridStackReset(dashboardsAppState);
    const refreshNecessary = selectRefreshNecessary(dashboardsAppState);

    if (this.widgetsRefreshing && shouldIgnoreLoadingWidgets) {
      this.widgetsRefreshing = true;
      if (!this.isCurrentlyRefreshing) {
        this.isCurrentlyRefreshing = true;
        await this.handleRefreshWidgets();
        this.isCurrentlyRefreshing = false;
      }
      return;
    }

    if (!this.widgetsRefreshing && refreshNecessary) {
      this.widgetsRefreshing = true;
      if (!this.isCurrentlyRefreshing) {
        this.isCurrentlyRefreshing = true;
        await this.handleRefreshWidgets();
        this.isCurrentlyRefreshing = false;
      }
      return;
    }

    if (this.widgetsRefreshing && !refreshNecessary) {
      this.widgetsRefreshing = false;
      return;
    }
  }

  private validateViewName(value: string, rootState: RootState): ValidationResult {
    const trimmedValue = value.trimStart().trimEnd();
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const allViews = selectAllSmartViews(dashboardsAppState);
    const escapedViewName = trimmedValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexp = new RegExp(`^${escapedViewName}$`, 'i');
    const matchingViews = allViews.filter(view => regexp.test(view.label));
    return {
      valid: matchingViews.length === 0,
      error: 'A view with this name already exists.',
    };
  }

  private handleDashboardActionsToolbarEvents() {
    if (this.dashboardSettingsRef) {
      if (this.dashboardUi.settingsPanelActive) {
        if (this.dashboardSettingsRef.classList.contains('hidden')) {
          removeElementClass(this.dashboardSettingsRef, 'hidden');
          this.openDashboardSettingsSideMenu();
        }
      }

      if (!this.dashboardUi.settingsPanelActive) {
        setElementClass(this.dashboardSettingsRef, 'hidden');
      }
    }
  }

  private handleDashboardWidgetsEvents() {
    if (this.dashboardWidgetDetailsRef) {
      if (this.dashboardUi.widgetDetailsPanelActive) {
        if (this.widgetDetailsComponent) {
          if (this.dashboardWidgetDetailsRef.classList.contains('hidden')) {
            removeElementClass(this.dashboardWidgetDetailsRef, 'hidden');
            this.dashboardWidgetDetailsSideMenuRef.setMenuContent(this.widgetDetailsComponent);
            this.dashboardWidgetDetailsSideMenuRef.openSideMenu();
          }
        }
      }

      if (!this.dashboardUi.widgetDetailsPanelActive) {
        setElementClass(this.dashboardWidgetDetailsRef, 'hidden');
      }
    }
  }

  render() {
    return this.dashboardConfiguration.smartViewsPanel.visible
      ? this.renderDashboardWithSmartViews()
      : this.renderDashboardWithoutSmartViews();
  }

  private onExpandViewButtonClick(): void {
    if (this.fullscreenActive) {
      exitFullscreen(() => {}, onExitFullscreenError.bind(this));
      store.dispatch(toggleFiltersPanelActiveState(true));
    } else {
      enterFullscreen(this.fullscreenContent, () => {}, onEnterFullscreenError.bind(this));
    }
  }

  private onFullscreenChange(): void {
    // when document goes from fullscreen mode to normal mode (via pressing the ESC button),
    // update the state accordingly, if not already updated
    if (!isBrowserFullscreen() && this.fullscreenActive) {
      store.dispatch(toggleFullscreenActiveState(false));
    }
  }

  private renderDashboardWithSmartViews() {
    const dashboardFiltersClasses = { separator: this.fullscreenActive && !this.filtersPanelActive };
    const gridStackManagerClasses = {
      fullScreenGridStack: this.fullscreenActive,
      filtersHidden: !this.dashboardUi.filtersPanelActive,
    };
    return html`
      <gz-push-body-layout
        @pushBodySidenavClose="${this.handleSmartViewsPanelClose}"
        @pushBodySidenavOpen="${this.handleSmartViewsPanelOpen}"
        .isOpen="${ifDefined(this.dashboardConfiguration.smartViewsPanel.isOpen) ?? true}"
      >
        <div slot="push-body-sidenav-title" class="border">
          <gz-title id="smartViewsTitle" fontweight="600" fontsize="11"
            >${ifDefined(this.dashboardConfiguration.smartViewsPanel.title.toUpperCase()) ?? 'SMART VIEWS'}
          </gz-title>
        </div>

        <div slot="push-body-sidenav">
          <div id="dashboard-smart-views" class="border">
            <gz-dashboard-smart-views-list
              class="pr-1 pl-1"
              ?hasSearchInput="${ifDefined(this.dashboardConfiguration.smartViewsPanel.hasSearch) ?? undefined}"
              .searchInputPlaceholder="${ifDefined(
                this.dashboardConfiguration.smartViewsPanel.searchInputPlaceholder
              ) ?? undefined}"
              .searchInputNoResultsFoundText="${ifDefined(
                this.dashboardConfiguration.smartViewsPanel.searchInputNoResultsFoundText
              ) ?? undefined}"
              .smartViewValidators="${this.smartViewsValidators}"
              ?disabled="${this.widgetsRefreshing}"
            ></gz-dashboard-smart-views-list>
          </div>
        </div>

        <div slot="push-body-content" id="fullscreen-content">
          <div
            id="dashboard-feedback-toolbar"
            class="${classMap({ 'feedback-toolbar': true, 'pdf-mode': this.displayMode === DisplayMode.PDF })}"
          >
            ${this.renderTitleWithFeedbackSection()}
          </div>
          <div id="dashboard-toolbar" class="${this.fullscreenActive && !this.filtersPanelActive ? 'separator' : ''}">
            <gz-dashboard-smart-views-toolbar
              ?disabled="${this.widgetsRefreshing}"
              @save="${this.handleSmartViewsSaveButtonClicked}"
              @saveAs="${this.handleSmartViewsSaveAsButtonClicked}"
              @discard="${this.handleSmartViewsDiscardButtonClicked}"
            ></gz-dashboard-smart-views-toolbar>
            <div id="dashboard-actions-toolbar">${this.renderDashboardActionsToolbar()}</div>
          </div>
          <div id="dashboard-filters">
            <gz-dashboard-filters-bar
              id="dashboard-filters-bar"
              ?disabled="${this.widgetsRefreshing}"
              displayMode="${this.displayMode}"
            >
            </gz-dashboard-filters-bar>
          </div>
          <div
            id="dashboard-grid-stack"
            class="${classMap({ border: true, 'pdf-mode': this.displayMode === DisplayMode.PDF })}"
          >
            <gz-dashboard-grid-stack-manager
              id="grid-stack-manager"
              dashboardType="${this.dashboardType}"
              displayMode="${this.displayMode}"
              class="${classMap(gridStackManagerClasses)}"
            ></gz-dashboard-grid-stack-manager>
          </div>
          <div id="dashboard-settings" class="border hidden">${this.renderDashboardSettings()}</div>
          <div id="dashboard-widget-details" class="border hidden">${this.renderDashboardWidgetDetails()}</div>
        </div>
      </gz-push-body-layout>
    `;
  }

  private renderDashboardWithoutSmartViews() {
    const dashboardFiltersClasses = { separator: this.fullscreenActive && !this.filtersPanelActive };
    const gridStackManagerClasses = {
      fullScreenGridStack: this.fullscreenActive,
      filtersHidden: !this.dashboardUi.filtersPanelActive,
      executiveSummary: this.dashboardConfiguration.dashboardType === DashboardType.EXECUTIVE_SUMMARY,
    };

    return html`
      <div slot="push-body-content" id="fullscreen-content">
        <div
          id="dashboard-feedback-toolbar"
          class="${classMap({ 'feedback-toolbar': true, 'pdf-mode': this.displayMode === DisplayMode.PDF })}"
        >
          ${this.renderTitleWithFeedbackSection()}
        </div>
        <div id="dashboard-toolbar" class="${this.fullscreenActive && !this.filtersPanelActive ? 'separator' : ''}">
          <div class="${this.dashboardConfiguration.resetView ? '' : 'hidden'}">
            <gz-generic-dashboard-reset-view> </gz-generic-dashboard-reset-view>
          </div>
          <div id="dashboard-actions-toolbar">${this.renderDashboardActionsToolbar()}</div>
        </div>
        <div id="dashboard-filters">
          <gz-dashboard-filters-bar
            id="dashboard-filters-bar"
            ?disabled="${this.widgetsRefreshing}"
            @selectionChange="${this.handleSmartViewsSaveButtonClicked}"
            @filtersChanged="${this.handleSmartViewsSaveButtonClicked}"
            @optionSelected="${this.handleSmartViewsSaveButtonClicked}"
            displayMode="${this.displayMode}"
          ></gz-dashboard-filters-bar>
        </div>
        <div
          id="dashboard-grid-stack"
          class="${classMap({ border: true, 'pdf-mode': this.displayMode === DisplayMode.PDF })}"
        >
          <gz-dashboard-grid-stack-manager
            id="grid-stack-manager"
            dashboardType="${this.dashboardType}"
            displayMode="${this.displayMode}"
            class="${classMap(gridStackManagerClasses)}"
            @gridAutoSaved="${this.handleSmartViewsSaveButtonClicked}"
            @widgetRemoved="${this.handleSmartViewsSaveButtonClicked}"
          ></gz-dashboard-grid-stack-manager>
        </div>
        <div id="dashboard-settings" class="border hidden">${this.renderDashboardSettings()}</div>
      </div>
    `;
  }

  private renderTitleWithFeedbackSection(): TemplateResult {
    return html`
      <gz-dashboard-title-with-feedback-toolbar
        @feedbackPopupOpen="${this.handleFeedBackButtonClick}"
        .isEarlyAccess="${this.isEarlyAccess}"
        .showFeedbackFormBtn="${this.showFeedbackFormBtn}"
        .hideFeedbackButton="${this.fullscreenActive}"
        .title="${this.getDashboardTitle()}"
        .companyName="${this.selectedTarget && this.selectedTarget.companyName ? this.selectedTarget.companyName : ''}"
        .displayMode="${this.displayMode}"
      ></gz-dashboard-title-with-feedback-toolbar>
    `;
  }

  private renderDashboardActionsToolbar(): TemplateResult {
    return html`
      <gz-dashboard-actions-toolbar
        ?disabled="${this.widgetsRefreshing}"
        @expandViewButtonClicked="${this.onExpandViewButtonClick}"
        @exportCsvOptionClicked="${this.onExportCsvButtonClick}"
        @exportPdfOptionClicked="${this.onExportPdfButtonClick}"
        @createReportButtonClicked="${this.onCreateReportButtonClick}"
        ?hasViewSettingsButton="${this.dashboardConfiguration.settingsPanel.visible}"
        ?hasLiveViewButton="${this.dashboardConfiguration.liveView.visible}"
        ?liveViewButtonUsable="${this.dashboardConfiguration.liveView.usable}"
        ?hasExportAsPdfOption="${this.dashboardConfiguration.canExport.pdf}"
        ?hasExportAsCsvOption="${this.dashboardConfiguration.canExport.csv}"
        ?hasCreateReportButton="${this.dashboardConfiguration.canExport.report}"
      ></gz-dashboard-actions-toolbar>
    `;
  }

  private renderDashboardSettings(): TemplateResult {
    return html`
      <gz-side-menu
        menuwidth="400"
        closeOnClickOutside
        id="dashboard-settings-side-menu"
        @close="${this.handleSettingsSideMenuClose}"
      ></gz-side-menu>
    `;
  }

  private renderDashboardWidgetDetails(): TemplateResult {
    return html`
      <gz-side-menu
        menuwidth="400"
        closeOnClickOutside
        id="dashboard-widget-details-side-menu"
        @close="${this.handleWidgetDetailsSideMenuClose}"
      ></gz-side-menu>
    `;
  }

  private getDashboardTitle(): string {
    switch (this.dashboardConfiguration.dashboardType) {
      case DashboardType.ASM:
        return translateFn('Attack surface management');
      case DashboardType.HEALTH_DASHBOARD:
        return translateFn('Health dashboard');
      case DashboardType.RISK_MANAGEMENT:
        return translateFn('Risk management');
      case DashboardType.EXECUTIVE_SUMMARY:
        return translateFn('Executive summary');
      default:
        return '';
    }
  }

  private async handleRefreshWidgets() {
    const activeWidgets = selectActiveWidgets(store.getState().dashboardsApp);
    const filters = selectDashboardFilterValues(store.getState().dashboardsApp);

    await this.streamWidgetsData(activeWidgets, filters);
  }

  private async streamWidgetsData(activeWidgets: DashboardReduxWidget[], filters: Array<Filter>) {
    if (this.gridStackManagerRef) {
      store.dispatch(toggleHasWidgetsInErrorState(false));
      this.widgetsInLoading = [];

      for (const widget of activeWidgets) {
        this.widgetsInLoading.push(widget.id);
        this.gridStackManagerRef.renderWidget(widget.id, {
          state: WidgetState.IS_LOADING,
        });
      }

      if (this.displayMode === DisplayMode.PDF) {
        this.signalWhenWidgetsLoadedForPdf(activeWidgets);
      }
    }

    const widgetIds: Array<string> = activeWidgets.map((widget: DashboardReduxWidget) => widget.id);
    try {
      const requestOptions: RequestOptions = getRequestDefaultOptions();
      requestOptions.requestTimeout = 120000; // 120 seconds

      const { input, output } = await MicroserviceRequest.makeRequest<DashboardStreamRequest, DashboardStreamResponse>(
        'Widgets',
        'getWidgetsDataForDashboard',
        this.getDashboardRequestParamsStream(widgetIds, filters),
        requestOptions
      );

      output.subscribe({
        next: (message: StreamMessage.Message) => this.handleWidgetsStreamResponse(message),
        error: error => this.handleStreamError(),
      });
    } catch (e) {
      this.handleStreamError();
    }
  }

  private isWidgetResponseFromCurrentDashboard(widgetId: string): boolean {
    for (const widget of this.gridStackManagerRef.widgets) {
      if (widget.id === widgetId) {
        return true;
      }
    }

    return false;
  }

  private signalWhenWidgetsLoadedForPdf(activeWidgets: DashboardReduxWidget[]): void {
    this.widgetsLoadedForPdf.clear();

    this.gridStackManagerRef.addEventListener(WidgetEvents.UPDATED, (event: CustomEvent) => {
      this.widgetsLoadedForPdf.add(event.detail.widgetId);

      if (activeWidgets.length !== this.widgetsLoadedForPdf.size) {
        return;
      }

      for (const widget of activeWidgets) {
        if (!this.widgetsLoadedForPdf.has(widget.id)) {
          console.error(`Got loaded event from ${activeWidgets.length} widgets, but widget ${widget.id} is missing`);
          return;
        }
      }

      this.dispatchEvent(new CustomEvent(this.widgetsLoadedForPdfEventName, { bubbles: true, composed: true }));
    });
  }

  private handleWidgetsStreamResponse(message: StreamMessage.Message): void {
    switch (message.type) {
      case StreamMessageType.DATA:
        if (!this.isWidgetResponseFromCurrentDashboard(message.data.widgetId)) {
          return;
        }
        this.handleStreamData(message as StreamMessage.StreamData<GetWidgetWithOverviewResponse>);
        break;
      case StreamMessageType.STREAM_STOP:
        this.handleStreamStop(message as StreamMessage.StreamStop);
        break;
      case StreamMessageType.STREAM_ERROR:
        this.handleStreamError();
        break;
      default:
        break;
    }
  }

  private handleStreamData(message: StreamMessage.StreamData<GetWidgetWithOverviewResponse>): void {
    const widgetData: GetWidgetWithOverviewResponse = message.data;

    this.gridStackManagerRef.renderWidget(widgetData.widgetId, {
      state: widgetData.state,
      data: { ...widgetData.data },
    });

    if (widgetData.state === WidgetState.IS_ERROR) {
      store.dispatch(toggleHasWidgetsInErrorState(true));
    }
    if (this.widgetsInLoading) {
      const widgetIndex = this.widgetsInLoading.indexOf(widgetData.widgetId);
      this.widgetsInLoading.splice(widgetIndex, 1);
    }
  }

  private handleStreamStop(message: StreamMessage.StreamStop): void {
    store.dispatch(setRefreshWidgets(false));
  }

  private handleStreamError(): void {
    for (const widgetId of this.widgetsInLoading) {
      this.gridStackManagerRef.renderWidget(widgetId, {
        state: WidgetState.IS_ERROR,
      });
    }
    this.widgetsInLoading = [];
    store.dispatch(toggleHasWidgetsInErrorState(true));
    store.dispatch(setRefreshWidgets(false));
  }

  private handleFeedBackButtonClick(): void {
    const feedbackType = this.dashboardConfiguration.feedbackType;
    if (feedbackType === undefined) {
      throw new Error('No feedback implemented for this dashboard type!');
    }

    let parentElement = document.querySelector(
      `gz-generic-dashboard-app[data-dashboard-type="${this.dashboardConfiguration.dashboardType}"]`
    ) as HTMLElement | null;
    if (parentElement) {
      parentElement = parentElement.parentElement;
    }
    FeedbackHelper.createFeedbackElement(
      feedbackType,
      DashboardPopUpType.FEEDBACK_VIEW_DIALOG_COMPLEX,
      this.dashboardConfiguration.dashboardType === DashboardType.HEALTH_DASHBOARD,
      parentElement
    );
  }

  private handleSmartViewsPanelClose(e: CustomEvent): void {
    store.dispatch(toggleSmartViewsPanelIsCollapsed(true));
  }

  private handleSmartViewsPanelOpen(e: CustomEvent): void {
    store.dispatch(toggleSmartViewsPanelIsCollapsed(false));
  }

  private handleSmartViewsSaveButtonClicked(e: CustomEvent): void {
    store.dispatch(saveSmartViewConfiguration());
    store.dispatch(toggleLiveViewActiveState(false));
  }

  private handleSmartViewsSaveAsButtonClicked(e: CustomEvent): void {
    DashboardPopUpFactory.createPopUp(
      DashboardPopUpType.SAVE_AS_DIALOG,
      (event: CustomEvent): void => {
        store.dispatch(createNewSmartView(event.detail.viewName.trimStart().trimEnd()));
        store.dispatch(toggleLiveViewActiveState(false));
      },
      (): void => {},
      {
        validators: this.smartViewsValidators,
      }
    );
  }

  private handleSmartViewsDiscardButtonClicked(e: CustomEvent): void {
    if (!this.shouldDisplayDiscardPopUp) {
      store.dispatch(discardSmartViewChanges());
      store.dispatch(toggleLiveViewActiveState(false));
      return;
    }

    DashboardPopUpFactory.createPopUp(DashboardPopUpType.DISCARD_CHANGES_DIALOG, async (event: CustomEvent): Promise<void> => {
      await this.dashboardFiltersBar.resetActiveCompany();
      store.dispatch(discardSmartViewChangesAsync(event.detail.showAgain));
      store.dispatch(toggleLiveViewActiveState(false));
    });
  }

  private handleSettingsSideMenuClose(e: CustomEvent): void {
    store.dispatch(toggleSettingsPanelActiveState(false));
    store.dispatch(updateWidgetsSearchText(''));
  }

  private handleWidgetDetailsSideMenuClose(e: CustomEvent): void {
    store.dispatch(toggleWidgetDetailsPanelActiveState(false));
  }

  private handleWidgetDetailsSideMenuOpen(e: CustomEvent): void {
    this.widgetDetailsComponent = e.detail.component;
    store.dispatch(toggleWidgetDetailsPanelActiveState(true));
  }

  private closeWidgetDetailsSideMenu(): void {
    this.widgetDetailsComponent = null;
    store.dispatch(toggleWidgetDetailsPanelActiveState(false));
  }

  private openDashboardSettingsSideMenu() {
    const gzDashboardSettings = new GzDashboardSettingsPanelWebComponent();
    if (this.dashboardConfiguration.settingsPanel.hasSearch) {
      gzDashboardSettings.hasSearchInput = this.dashboardConfiguration.settingsPanel.hasSearch;
    }

    if (this.dashboardConfiguration.settingsPanel.searchInputPlaceholder) {
      gzDashboardSettings.searchInputPlaceholder = this.dashboardConfiguration.settingsPanel.searchInputPlaceholder;
    }

    if (this.dashboardConfiguration.settingsPanel.searchInputNoResultsFoundText) {
      gzDashboardSettings.noWidgetsFoundMessage =
        this.dashboardConfiguration.settingsPanel.searchInputNoResultsFoundText;
    }

    this.dashboardSettingsSideMenuRef.setMenuContent(gzDashboardSettings);
    this.dashboardSettingsSideMenuRef.openSideMenu();

    if (!this.sideMenuListenersAdded) {
      this.dashboardSettingsSideMenuRef.addEventListener(GDSettingsPanelEvents.APPLY_BUTTON_CLICKED, () => {
        this.dashboardSettingsSideMenuRef.closeSideMenu();
      });

      this.dashboardSettingsSideMenuRef.addEventListener(GDSettingsPanelEvents.RESET_WIDGETS_BUTTON_CLICKED, () => {
        this.dashboardSettingsSideMenuRef.closeSideMenu();
      });

      this.sideMenuListenersAdded = true;
    }
  }

  private getDashboardRequestParamsStream(widgetIds: Array<string>, filters: Array<Filter>): DashboardStreamRequest {
    return {
      widgetIds,
      filters,
      locale: Application.Main.getLanguage(),
    };
  }

  private async onExportCsvButtonClick(event: CustomEvent): Promise<void> {
    const exportButton = event.target as GzIconButtonWebComponent;
    store.dispatch(toggleExportButtonSpinner(true));

    const activeWidgets = selectActiveWidgets(store.getState().dashboardsApp);
    const filters = selectDashboardFilterValues(store.getState().dashboardsApp);

    await this.streamCsv(activeWidgets, filters, exportButton);
  }

  private async onCreateReportButtonClick(): Promise<void> {
    const filters = selectDashboardFilterValues(store.getState().dashboardsApp);

    try {
      switch (this.dashboardType) {
        case DashboardType.EXECUTIVE_SUMMARY:
          const { timeRange, selectedTarget } = await getExecutiveSummaryParams(filters);

          NavigationHelper.showReportForm(this.dependencies.Ext, timeRange, selectedTarget);
          break;
        default:
          this.buildToastNotification(
            translateFn('The report functionality is not supported for this dashboard type.'),
            ToastSeverity.INFORMATIVE
          );
          break;
      }
    } catch (exception) {
      this.buildToastNotification(translateFn('Failed to create the report file'), ToastSeverity.ERROR);
    }
  }

  private async onExportPdfButtonClick(event: CustomEvent): Promise<void> {
    const exportButton = event.target as GzIconButtonWebComponent;
    store.dispatch(toggleExportButtonSpinner(true));

    const activeWidgets = selectActiveWidgets(store.getState().dashboardsApp);
    const filters = selectDashboardFilterValues(store.getState().dashboardsApp);

    await this.getPdf(activeWidgets, filters, exportButton);
  }

  private async streamCsv(
    activeWidgets: DashboardReduxWidget[],
    filters: Filter[],
    exportButton: GzIconButtonWebComponent
  ): Promise<void> {
    const widgetIds = activeWidgets.map((activeWidget: DashboardReduxWidget) => activeWidget.id);

    try {
      const { output } = await MicroserviceRequest.makeRequest<GetDashboardCsvRequest, DashboardStreamResponse>(
        'Widgets',
        'getCsvDataForDashboard',
        this.getCsvDataRequestParams(widgetIds, filters)
      );
      output.subscribe({
        next: (message: StreamMessage.Message) => this.handleCsvStreamResponse(message, exportButton),
        error: error => this.handleStreamErrorCsv(error as NirvanaSanitizedError),
      });
    } catch (exception) {
      this.handleStreamErrorCsv(exception);
    }
  }

  private async getPdf(
    activeWidgets: DashboardReduxWidget[],
    filters: Filter[],
    exportButton: GzIconButtonWebComponent
  ): Promise<void> {
    const widgetIds = activeWidgets.map((activeWidget: DashboardReduxWidget) => activeWidget.id);

    try {
      const response = await MicroserviceRequest.makeRequest<GetDashboardPdfRequest, GetDashboardPdfResponse>(
        'GenericDashboards',
        'getPdfDataForDashboard',
        this.getPdfDataRequestParams(widgetIds, filters)
      );

      const pdfTime = getFormattedDateTime(
        TimestampFormat.FILES,
        Date.now(),
        Application.Main.getUser().profile.timezone ?? 'UTC'
      );

      const pdfName = `${response.pdfNamePrefix}${Application.Main.getLanguage()}_${pdfTime}.pdf`;

      const blob = new Blob([response.pdfData], { type: 'application/pdf' });
      downloadBlob(blob, pdfName);

      this.buildToastNotification(
        translateFn('Successfully exported {fileName}', { fileName: pdfName }),
        ToastSeverity.SUCCESS
      );

      store.dispatch(toggleExportButtonSpinner(false));
    } catch (exception) {
      this.handleGetPdfError(exception);
    }
  }

  private handleCsvStreamResponse(message: StreamMessage.Message, exportButton: GzIconButtonWebComponent): void {
    switch (message.type) {
      case StreamMessageType.DATA:
        this.handleCsvStreamData(message as StreamMessage.StreamData<GetDashboardCsvResponse>);
        break;
      case StreamMessageType.STREAM_STOP:
        this.handleCsvStreamStop(message as StreamMessage.StreamStop);
        break;
      case StreamMessageType.STREAM_ERROR:
        this.handleStreamErrorCsv(message as StreamMessage.StreamError);
        break;
      default:
        break;
    }
  }

  private handleCsvStreamData(message: StreamMessage.StreamData<GetDashboardCsvResponse>): void {
    this.csvParts.push(message.data.csvData);
    this.csvPrefix = this.csvPrefix ?? message.data.csvNamePrefix;
  }

  private handleCsvStreamStop(message: StreamMessage.StreamStop) {
    this.csvParts.unshift('\ufeff'); // send UTF-8 header to encode file properly

    const csvTime = getFormattedDateTime(
      TimestampFormat.FILES,
      Date.now(),
      Application.Main.getUser().profile.timezone ?? 'UTC'
    );

    const csvName = `${this.csvPrefix}${Application.Main.getLanguage()}_${csvTime}.csv`;

    const blob = new Blob(this.csvParts, { type: 'text/csv' });
    downloadBlob(blob, csvName);
    this.csvParts.length = 0;

    this.buildToastNotification(
      translateFn('Successfully exported {fileName}', { fileName: csvName }),
      ToastSeverity.SUCCESS
    );

    store.dispatch(toggleExportButtonSpinner(false));
  }

  private handleStreamErrorCsv(error: any): void {
    switch (error.code as NirvanaErrorCode) {
      case NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE:
        this.buildToastNotification(
          translateFn('The export functionality is temporarily disabled. Please try again later.'),
          ToastSeverity.INFORMATIVE
        );
        break;
      case NirvanaErrorCode.INVALID_DATA:
        this.buildToastNotification(
          translateFn('Failed to export. The filters contain invalid data.'),
          ToastSeverity.WARNING
        );
        break;
      default:
        this.buildToastNotification(translateFn('Failed to export file'), ToastSeverity.ERROR);
        break;
    }

    store.dispatch(toggleExportButtonSpinner(false));
  }

  private handleGetPdfError(error: any): void {
    switch (error.code as NirvanaErrorCode) {
      case NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE:
        this.buildToastNotification(
          translateFn('The export functionality is not supported.'),
          ToastSeverity.INFORMATIVE
        );
        break;
      case NirvanaErrorCode.INVALID_DATA:
        this.buildToastNotification(
          translateFn('Failed to export. The filters contain invalid data.'),
          ToastSeverity.WARNING
        );
        break;
      default:
        this.buildToastNotification(translateFn('Failed to export file'), ToastSeverity.ERROR);
        break;
    }

    store.dispatch(toggleExportButtonSpinner(false));
  }

  private buildToastNotification(message: string, severity: ToastSeverity) {
    const toastNotificationConfigs: GzToast = {
      message,
      hasAction: false,
      severity: severity,
      keepOpen: false,
      autoCloseDelayMs: 10000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  private getCsvDataRequestParams(widgetIds: Array<string>, filters: Array<Filter>): GetDashboardCsvRequest {
    return {
      widgetIds,
      filters,
      locale: Application.Main.getLanguage(),
      dashboardType: this.dashboardConfiguration.dashboardType,
    };
  }

  private getPdfDataRequestParams(widgetIds: Array<string>, filters: Array<Filter>): GetDashboardPdfRequest {
    const widgetsPayload = new Array<WidgetsPayloadMapData>();
    widgetIds.forEach((widgetId: string) => {
      const widget = this.gridStackManagerRef.shadowRoot?.querySelector(widgetId) as HTMLElement | null;
      const widgetPayload = widget?.getAttribute('payload') ?? '';
      const widgetState =
        WidgetStateFrontEndToBackend.get(widget?.getAttribute('state') as WidgetStateFrontEnd) ?? WidgetState.IS_ACTIVE;
      widgetsPayload.push({ widgetId: widgetId, payload: widgetPayload, state: widgetState });
    });

    let selectedTarget: Target;
    if (FiltersHelper.shouldDisplayCompanyFilter()) {
      const selectedCompany = this.dashboardFiltersBar.$companyFilter.getSelectedOption();
      selectedTarget = {
        companyName: selectedCompany?.label ?? '',
        companyId: (selectedCompany?.value ?? '') as string,
      };
    } else {
      selectedTarget = {
        companyName: Application.Main.getUserCompany()?.name ?? '',
        companyId: Application.Main.getUserCompany()?.id ?? '',
      };
    }

    const getPageFormat = (): PageFormat => {
      const width = this.offsetWidth;
      let height = 0;

      // Add the height of the dashboard title
      if (this.dashboardFeedbackToolbarRef) {
        height += this.dashboardFeedbackToolbarRef.offsetHeight;
      }

      // Add the height of the reporting period banner, if present
      const reportingPeriodElement = this.dashboardFiltersBar?.shadowRoot?.querySelector(
        'gz-reporting-period'
      ) as HTMLElement | null;
      if (reportingPeriodElement) {
        height += reportingPeriodElement.offsetHeight;
      }

      // Add the height of the grid-stack-manager component, which contains all the widgets
      const gridWrapper = this.gridStackManagerRef.shadowRoot?.querySelector('.grid-wrapper') as HTMLElement | null;
      if (gridWrapper) {
        height += gridWrapper.offsetHeight;
      }

      return { width: width, height: height, isLandscape: false };
    };

    return {
      filters,
      widgetsPayload,
      userConfig: {
        dashboardType: this.dashboardConfiguration.dashboardType,
        activeViewProps: selectActiveViewProps(store.getState().dashboardsApp),
        uiConfig: this.dashboardConfiguration,
        smartViews: selectAllSmartViews(store.getState().dashboardsApp),
        smartViewsCategories: selectAllSmartViewsCategories(store.getState().dashboardsApp),
        filterConfigs: selectDashboardFilterConfigs(store.getState().dashboardsApp),
        widgetsCategories: selectWidgetCategories(store.getState().dashboardsApp),
        availableWidgets: selectWidgets(store.getState().dashboardsApp),
      },
      componentLoadedEventName: this.widgetsLoadedForPdfEventName,
      globalThisApplication: {
        Main: {
          getLanguage: Application.Main.getLanguage(),
          getUser: Application.Main.getUser(),
          company: {
            isPartner: Application.Main.company.isPartner(),
          },
          hasAnyRights: Application.Main.hasAnyRights([Rights.RIGHT_MANAGE_INVENTORY]),
        },
      },
      selectedTarget: selectedTarget,
      locale: Application.Main.getLanguage(),
      timezone: Application.Main.getUser().profile.timezone ?? 'UTC',
      pageFormat: getPageFormat(),
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dashboard-macro-component': GzDashboardMacroComponentWebComponent;
  }
}
